/**
 * Stack - Header Menu JavaScript
 * Web Components for the Stack Header Menu.
 * 
 * Requirements: 9.4, 9.5, 3.6, 4.1, 4.3, 4.4, 5.2, 5.3, 5.4, 8.1, 8.3, 8.4, 8.5, 8.6
 */

/**
 * StackHeaderMenu - Main header component
 * Orchestrates the header behavior including cart updates and drawer toggle.
 * Requirements: 9.4, 9.5, 3.6
 */
class StackHeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint) || 1099;
    this.isDrawerOpen = false;
    this.drawer = null;
    this.drawerToggle = null;
    this.cartCountElement = null;
    this.unsubscribeCart = null;
    this.resizeHandler = this.handleResize.bind(this);
  }

  connectedCallback() {
    this.drawer = this.querySelector('stack-mobile-drawer');
    this.drawerToggle = this.querySelector('[data-drawer-toggle]');
    this.cartCountElement = this.querySelector('[data-stack-cart-count]');
    this.cartLink = this.querySelector('[data-stack-cart-link]');

    // Force header-height to 0 so content starts from top of page
    // This overrides the original header's dynamic height setting
    this.forceHeaderHeightZero();

    // Setup hamburger button to toggle drawer
    // Requirements: 4.1, 4.3
    if (this.drawerToggle) {
      this.drawerToggle.addEventListener('click', () => this.toggleDrawer());
    }

    // Setup cart link to open drawer
    this.setupCartDrawerLink();

    // Subscribe to cart updates via PubSub
    // Requirements: 3.6
    if (typeof subscribe === 'function') {
      this.unsubscribeCart = subscribe('cart:update', (cartData) => {
        if (cartData && typeof cartData.item_count !== 'undefined') {
          this.updateCartCount(cartData.item_count);
        }
      });

      // Also subscribe to cartUpdate for product-form compatibility
      if (typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.syncCartCountFromHiddenBubble();
        });
      }

      // Subscribe to drawer close events to sync state
      this.unsubscribeDrawerClose = subscribe('stack-header:drawer-close', () => {
        this.isDrawerOpen = false;
        if (this.drawerToggle) {
          this.drawerToggle.setAttribute('aria-expanded', 'false');
        }
      });
    }

    // Setup MutationObserver to watch for cart count updates in hidden bubble
    this.setupCartCountObserver();

    // Handle resize for breakpoint changes
    window.addEventListener('resize', this.resizeHandler);

    // Setup keyboard navigation for desktop menu
    this.setupKeyboardNavigation();
  }

  disconnectedCallback() {
    // Cleanup listeners
    if (this.unsubscribeCart) {
      this.unsubscribeCart();
    }
    if (this.unsubscribeCartUpdate) {
      this.unsubscribeCartUpdate();
    }
    if (this.unsubscribeDrawerClose) {
      this.unsubscribeDrawerClose();
    }
    window.removeEventListener('resize', this.resizeHandler);
    
    // Cleanup header height observer
    if (this._headerHeightObserver) {
      this._headerHeightObserver.disconnect();
    }
    
    // Cleanup cart count observer
    if (this._cartCountObserver) {
      this._cartCountObserver.disconnect();
    }
  }

  /**
   * Setup MutationObserver to watch for changes in the hidden cart-icon-bubble
   * This syncs the visible cart count when the Section Rendering API updates it
   */
  setupCartCountObserver() {
    const hiddenBubble = document.getElementById('cart-icon-bubble');
    if (!hiddenBubble) return;

    this._cartCountObserver = new MutationObserver(() => {
      this.syncCartCountFromHiddenBubble();
    });

    this._cartCountObserver.observe(hiddenBubble, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /**
   * Sync cart count from the hidden cart-icon-bubble element
   */
  syncCartCountFromHiddenBubble() {
    const hiddenBubble = document.getElementById('cart-icon-bubble');
    if (!hiddenBubble) return;

    // Find the cart count in the hidden bubble
    const countElement = hiddenBubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
    if (countElement) {
      const count = parseInt(countElement.textContent, 10) || 0;
      this.updateCartCount(count);
    }
  }

  /**
   * Setup cart link to open cart drawer instead of navigating to cart page
   */
  setupCartDrawerLink() {
    if (!this.cartLink) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return; // Cart drawer not present, use default link behavior

    // Set up the link to open the drawer
    this.cartLink.setAttribute('role', 'button');
    this.cartLink.setAttribute('aria-haspopup', 'dialog');

    this.cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      cartDrawer.open(this.cartLink);
    });

    this.cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        cartDrawer.open(this.cartLink);
      }
    });
  }

  toggleDrawer() {
    if (!this.drawer) return;

    this.isDrawerOpen = !this.isDrawerOpen;

    if (this.isDrawerOpen) {
      this.drawer.open();
    } else {
      this.drawer.close();
    }

    // Update toggle button aria-expanded
    if (this.drawerToggle) {
      this.drawerToggle.setAttribute('aria-expanded', this.isDrawerOpen.toString());
    }
  }

  updateCartCount(count) {
    if (!this.cartCountElement) return;

    const numCount = parseInt(count, 10) || 0;
    this.cartCountElement.textContent = numCount;

    // Toggle hidden class based on count
    if (numCount === 0) {
      this.cartCountElement.classList.add('stack-header__cart-count--hidden');
    } else {
      this.cartCountElement.classList.remove('stack-header__cart-count--hidden');
    }
  }

  handleResize() {
    const isMobile = window.innerWidth <= this.mobileBreakpoint;

    // Close drawer when switching to desktop
    if (!isMobile && this.isDrawerOpen && this.drawer) {
      this.drawer.close();
      this.isDrawerOpen = false;
      if (this.drawerToggle) {
        this.drawerToggle.setAttribute('aria-expanded', 'false');
      }
    }
  }

  setupKeyboardNavigation() {
    // Setup keyboard navigation for desktop dropdown menus
    const menuButtons = this.querySelectorAll('.stack-header__menu-link--toggle');

    menuButtons.forEach((button) => {
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isExpanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', (!isExpanded).toString());
        }

        if (e.key === 'Escape') {
          button.setAttribute('aria-expanded', 'false');
          button.focus();
        }
      });

      // Close dropdown when clicking outside
      button.addEventListener('blur', () => {
        // Delay to allow click on dropdown items
        setTimeout(() => {
          const dropdown = button.nextElementSibling;
          if (dropdown && !dropdown.contains(document.activeElement)) {
            button.setAttribute('aria-expanded', 'false');
          }
        }, 150);
      });
    });

    // Setup arrow key navigation for menu items
    this.setupArrowKeyNavigation();
  }

  setupArrowKeyNavigation() {
    const menuItems = this.querySelectorAll('.stack-header__menu-item');

    menuItems.forEach((item) => {
      const link = item.querySelector('.stack-header__menu-link');
      if (!link) return;

      link.addEventListener('keydown', (e) => {
        const allLinks = Array.from(this.querySelectorAll('.stack-header__menu > .stack-header__menu-item > .stack-header__menu-link'));
        const currentIndex = allLinks.indexOf(link);

        switch (e.key) {
          case 'ArrowRight':
            e.preventDefault();
            const nextIndex = (currentIndex + 1) % allLinks.length;
            allLinks[nextIndex].focus();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            const prevIndex = (currentIndex - 1 + allLinks.length) % allLinks.length;
            allLinks[prevIndex].focus();
            break;
          case 'ArrowDown':
            // If this is a dropdown toggle, open it and focus first item
            if (link.classList.contains('stack-header__menu-link--toggle')) {
              e.preventDefault();
              link.setAttribute('aria-expanded', 'true');
              const dropdown = link.nextElementSibling;
              if (dropdown) {
                const firstSubmenuLink = dropdown.querySelector('.stack-header__submenu-link, .stack-mega__link');
                if (firstSubmenuLink) {
                  firstSubmenuLink.focus();
                }
              }
            }
            break;
        }
      });
    });
  }

  /**
   * Force --header-height CSS variable to 0px
   * This ensures content starts from the top of the page and passes under the fixed header.
   * We use MutationObserver to continuously override any changes from the original header script.
   */
  forceHeaderHeightZero() {
    const setHeaderHeightZero = () => {
      document.documentElement.style.setProperty('--header-height', '0px', 'important');
      document.documentElement.style.setProperty('--header-height-top', '0px', 'important');
      document.documentElement.style.setProperty('--header-height-bottom', '0px', 'important');
    };

    // Set immediately
    setHeaderHeightZero();

    // Also set after a short delay to override any scripts that run after DOMContentLoaded
    setTimeout(setHeaderHeightZero, 100);
    setTimeout(setHeaderHeightZero, 500);

    // Use MutationObserver to catch any dynamic changes to the style attribute
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style') {
          const currentHeight = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
          if (currentHeight && currentHeight.trim() !== '0px') {
            setHeaderHeightZero();
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });

    // Store observer reference for cleanup
    this._headerHeightObserver = observer;
  }
}


/**
 * StackMobileDrawer - Mobile navigation drawer component
 * Handles slide animation and body scroll prevention.
 * Requirements: 4.1, 4.3, 4.4
 */
class StackMobileDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.focusableElements = [];
    this.previousActiveElement = null;
    this.closeButton = null;
    this.overlay = null;
    this.escapeHandler = this.handleEscape.bind(this);
    this.focusTrapHandler = this.handleFocusTrap.bind(this);
  }

  connectedCallback() {
    this.closeButton = this.querySelector('[data-drawer-close]');
    this.overlay = this.querySelector('[data-drawer-overlay]');

    // Setup close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Setup overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.previousActiveElement = document.activeElement;

    // Update ARIA state
    this.setAttribute('aria-hidden', 'false');

    // Prevent body scroll
    this.preventBodyScroll(true);

    // Setup focus trap and escape handler
    this.setupFocusTrap();
    document.addEventListener('keydown', this.escapeHandler);

    // Publish event
    if (typeof publish === 'function') {
      publish('stack-header:drawer-open', { isOpen: true });
    }

    // Focus close button after animation
    requestAnimationFrame(() => {
      if (this.closeButton) {
        this.closeButton.focus();
      }
    });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Update ARIA state
    this.setAttribute('aria-hidden', 'true');

    // Restore body scroll
    this.preventBodyScroll(false);

    // Remove focus trap and escape handler
    document.removeEventListener('keydown', this.escapeHandler);
    document.removeEventListener('keydown', this.focusTrapHandler);

    // Publish event - parent header subscribes to sync state
    // Requirements: 4.3
    if (typeof publish === 'function') {
      publish('stack-header:drawer-close', { isOpen: false });
    }

    // Restore focus to trigger element
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
      this.previousActiveElement = null;
    }
  }

  preventBodyScroll(prevent) {
    if (prevent) {
      document.body.classList.add('stack-drawer-open');
    } else {
      document.body.classList.remove('stack-drawer-open');
    }
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close();
    }
  }

  setupFocusTrap() {
    // Get all focusable elements within the drawer
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this.focusableElements = Array.from(this.querySelectorAll(focusableSelectors));
    document.addEventListener('keydown', this.focusTrapHandler);
  }

  handleFocusTrap(event) {
    if (event.key !== 'Tab' || !this.isOpen) return;

    const firstFocusable = this.focusableElements[0];
    const lastFocusable = this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab: if on first element, go to last
      if (document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
    } else {
      // Tab: if on last element, go to first
      if (document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  }
}


/**
 * StackAccordionMenu - Expandable menu item component
 * Handles expand/collapse animation for mobile submenus.
 * Requirements: 5.2, 5.3, 5.4
 */
class StackAccordionMenu extends HTMLElement {
  constructor() {
    super();
    this.isExpanded = false;
    this.trigger = null;
    this.content = null;
    this.contentHeight = 0;
  }

  connectedCallback() {
    this.trigger = this.querySelector('.stack-accordion__trigger');
    this.content = this.querySelector('.stack-accordion__content');

    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.toggle());
      
      // Keyboard support for accordion
      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggle();
        }
      });
    }

    // Calculate content height for animation
    this.calculateContentHeight();
  }

  toggle() {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.expand();
    } else {
      this.collapse();
    }

    this.updateAriaAttributes();
  }

  expand() {
    if (!this.content) return;

    // Recalculate height in case content changed
    this.calculateContentHeight();

    // Set the height for animation
    this.content.style.setProperty('--accordion-content-height', `${this.contentHeight}px`);
    this.content.setAttribute('aria-hidden', 'false');
  }

  collapse() {
    if (!this.content) return;

    this.content.setAttribute('aria-hidden', 'true');
  }

  calculateContentHeight() {
    if (!this.content) return;

    // Temporarily show content to measure height
    const wasHidden = this.content.getAttribute('aria-hidden') === 'true';

    if (wasHidden) {
      this.content.style.maxHeight = 'none';
      this.content.style.visibility = 'hidden';
      this.content.style.position = 'absolute';
    }

    this.contentHeight = this.content.scrollHeight;

    if (wasHidden) {
      this.content.style.maxHeight = '';
      this.content.style.visibility = '';
      this.content.style.position = '';
    }
  }

  updateAriaAttributes() {
    if (this.trigger) {
      this.trigger.setAttribute('aria-expanded', this.isExpanded.toString());
    }
  }
}

// Register custom elements
if (!customElements.get('stack-header-menu')) {
  customElements.define('stack-header-menu', StackHeaderMenu);
}

if (!customElements.get('stack-mobile-drawer')) {
  customElements.define('stack-mobile-drawer', StackMobileDrawer);
}

if (!customElements.get('stack-accordion-menu')) {
  customElements.define('stack-accordion-menu', StackAccordionMenu);
}
